import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const isAuthenticated = !!token && !!user;

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, marketingOptIn = false) => {
    const response = await api.post('/auth/register', { 
      email, 
      password,
      marketing_opt_in: marketingOptIn
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSubscriptionStatus(null);
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to check subscription:', error);
      return null;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
  };

  const dismissUpgradeNotification = async () => {
    try {
      await api.post('/auth/dismiss-upgrade-notification');
      setUser(prev => prev ? { ...prev, has_unread_upgrade_notification: false } : prev);
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    subscriptionStatus,
    login,
    register,
    logout,
    refreshUser,
    checkSubscription,
    changePassword,
    dismissUpgradeNotification
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
